import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLazyMeQuery, useResendVerificationMutation } from "../api/authApi";
import { getDefaultRouteForUser } from "../utils/redirect";

const STORAGE_KEY = "logbook_auth_user_v2";

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const verified = params.get("verified") === "1";

  const [resend, { isLoading: sending }] = useResendVerificationMutation();
  const [triggerMe, { isFetching }] = useLazyMeQuery();

  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (verified) setMsg("Email verified successfully. Click continue.");
  }, [verified]);

  const onResend = async () => {
    setErr(null);
    setMsg(null);
    try {
      const res = await resend().unwrap();
      setMsg(res?.message || "Verification email sent.");
    } catch (e: any) {
      setErr(e?.data?.message || e?.message || "Failed to resend email.");
    }
  };

  const onRefresh = async () => {
    setErr(null);
    setMsg(null);

    try {
      const res = await triggerMe().unwrap();
      const user = res?.user;

      if (!user) {
        setErr("Unable to fetch user.");
        return;
      }

      // ✅ update local storage with fresh user status (keep token)
      const token = localStorage.getItem("token");
      const authData = { ...user, token };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

      // ✅ will send verified probationer to dashboard
      navigate(getDefaultRouteForUser(), { replace: true });
    } catch (e: any) {
      setErr(e?.data?.message || e?.message || "Failed to refresh status.");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(STORAGE_KEY);
    navigate("/login", { replace: true });
  };

  return (
    <div className="w-full">
      <h2 className="text-lg font-semibold text-slate-900">
        Verify your email
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        We sent a verification link to your email. Please verify to continue.
      </p>

      {err && (
        <div className="mt-4 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {err}
        </div>
      )}

      {msg && (
        <div className="mt-4 text-xs font-semibold text-green-700 bg-green-50 border border-green-100 rounded-md px-3 py-2">
          {msg}
        </div>
      )}

      <div className="mt-6 space-y-3">
        <button
          onClick={onResend}
          disabled={sending}
          className="w-full py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
        >
          {sending ? "Sending..." : "Resend verification email"}
        </button>

        <button
          onClick={onRefresh}
          disabled={isFetching}
          className="w-full py-2.5 text-sm font-medium bg-slate-100 text-slate-800 rounded-md hover:bg-slate-200 disabled:opacity-60"
        >
          {isFetching ? "Checking..." : "I have verified, continue"}
        </button>

        <button
          onClick={logout}
          className="w-full py-2.5 text-sm font-medium bg-white border border-slate-200 text-slate-800 rounded-md hover:bg-slate-50"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
